import type { CreateDealPayload, SCICommonConsumer, UpdateDealPayload } from '@makemydeal/dr-sales-checkin-types';
import type { DealVersionChangeType } from '@makemydeal/dr-shared-bff-types';

//interface/types
import type { RelationShip } from '@makemydeal/dr-shared-bff-types';

import { isEmpty } from 'lodash';
import { CORRECT_PHONE_HANDLING_TOGGLE } from '@makemydeal/dr-sales-checkin-types';
import { resolvePrimaryPhone } from '@makemydeal/dr-shared-ui-utils/phone/resolvePhone';
import { sanitizePostalCode, sanitizePhoneNumber } from './helpers';

const resolveDealPrimaryPhone = (consumer: SCICommonConsumer, state: any): string => {
    const enableCorrectPhoneHandling = Boolean(
        (state.featureToggles as Record<string, boolean | undefined> | undefined)?.[CORRECT_PHONE_HANDLING_TOGGLE]
    );

    if (!enableCorrectPhoneHandling) return sanitizePhoneNumber(consumer.cellPhone) || '';

    return sanitizePhoneNumber(
        resolvePrimaryPhone({
            cellPhone: consumer.cellPhone,
            homePhone: consumer.homePhone,
            workPhone: consumer.workPhone
        })
    );
};

/**
 * Maps and Create Deal payload from the state.
 * `state` is typed `any` because the two app-level AppState variants (activities-local
 * and shared sales-checkin-types) diverge on `activeDeal.details` (DealXG), so neither is
 * assignable to the other. All reads are structural and store-free — reading state directly
 * here (rather than importing dash-store selectors) avoids a ui-utils -> store cycle, and is
 * consistent with `getDealXgId`/`salesViewMode` below.
 * TODO: [HL]- WE NEED TO GET THIS MAPPER SORTED
 */
export const buildCreateDealPayload = (state: any): CreateDealPayload => {
    const customer = state.salesCheckin.customer.checkedInCustomer as SCICommonConsumer;
    const selectedDxgVehicle = state.salesCheckin.deals.selectedDxgVehicle;
    const coBuyerInfo = state.salesCheckin.customer.coBuyer as SCICommonConsumer;
    const dealerDetails = state.dealer || {};
    // Only a vehicle-change save is picking a *new* vehicle (captured in deals.selectedDxgVehicle
    // ahead of the save, before it lands on the deal). Every other save should leave the deal's
    // vehicle untouched: selectedDxgVehicle can be stale — e.g. a vehicle added from the MV side
    // never flows into that slice — so send the deal's own sellingVehicle as-is when it has one.
    const vehicleInfo = isEmpty(selectedDxgVehicle) ? undefined : selectedDxgVehicle;
    const postalCode = customer.postalCode && sanitizePostalCode(customer.postalCode);
    const buyerAddress = {
        postalCode: postalCode,
        city: customer.city || '',
        stateProvince: customer.stateOrProvinceAbbreviation || customer.stateOrProvince || '',
        countyDistrict: customer.countyOrRegion || '',
        country: customer.country || '',
        addressLine1: customer.street1 || '',
        addressLine2: customer.street2 || ''
    };

    const cobuyerAddress = coBuyerInfo
        ? {
              postalCode: coBuyerInfo.postalCode && sanitizePostalCode(coBuyerInfo.postalCode),
              city: coBuyerInfo.city || '',
              stateProvince: coBuyerInfo.stateOrProvince || '',
              countyDistrict: coBuyerInfo.countyOrRegion || '',
              country: coBuyerInfo.country || '',
              addressLine1: coBuyerInfo.street1 || '',
              addressLine2: coBuyerInfo.street2 || ''
          }
        : undefined;

    return {
        customerInfo: {
            suffix: customer.suffix || '',
            salutation: customer.title || '',
            consumerId: customer.consumerId || '',
            firstName: customer.firstName,
            middleName: customer.middleName,
            lastName: customer.lastName,
            email: customer.primaryEmail,
            phone: resolveDealPrimaryPhone(customer, state),
            cellPhone: sanitizePhoneNumber(customer.cellPhone),
            homePhone: sanitizePhoneNumber(customer.homePhone),
            workPhone: sanitizePhoneNumber(customer.workPhone),
            dealershipId: customer.dealershipId,
            address: buyerAddress,
            legalName: customer.legalName || ''
        },
        vehicleInfo,
        salespersonInfo: {
            bridgeId: state.bridgeUser?.id || '',
            firstName: state.bridgeUser?.givenName || '',
            lastName: state.bridgeUser?.familyName || '',
            userName: state.bridgeUser?.username || ''
        },
        dealer: {
            // TODO: HARRIER: We need to correct the types of dealerMMDID.
            // Currently, server is returning dealerMMDID as number but in our types it is string.
            mmdId: dealerDetails.dealerMMDId as unknown as string
        },
        coBuyer: coBuyerInfo
            ? {
                  salutation: coBuyerInfo.title || '',
                  consumerId: coBuyerInfo.consumerId || '',
                  firstName: coBuyerInfo.firstName,
                  lastName: coBuyerInfo.lastName,
                  middleName: coBuyerInfo.middleName || '',
                  email: coBuyerInfo.primaryEmail,
                  phone: resolveDealPrimaryPhone(coBuyerInfo, state),
                  cellPhone: sanitizePhoneNumber(coBuyerInfo.cellPhone),
                  homePhone: sanitizePhoneNumber(coBuyerInfo.homePhone),
                  workPhone: sanitizePhoneNumber(coBuyerInfo.workPhone),
                  postalCode: coBuyerInfo.postalCode && sanitizePostalCode(coBuyerInfo.postalCode),
                  dealershipId: coBuyerInfo.dealershipId,
                  suffix: coBuyerInfo.suffix || '',
                  address: cobuyerAddress,
                  relationshipToBuyer: (coBuyerInfo.relationshipToBuyer as RelationShip) || 'Other'
              }
            : null
    };
};

/**
 * Maps and Create Deal update payload from the state
 */
export const buildUpdateDealPayload = (state: any, changeType?: DealVersionChangeType): UpdateDealPayload => {
    const createDealPayload = buildCreateDealPayload(state);
    const activeDealDetails = state.salesCheckin.activeDeal?.details;
    const dealStructure = activeDealDetails?.dealStructure;
    const acceptedByBuyer = dealStructure?.acceptedByBuyer;
    const visibility = dealStructure?.visibility;
    return {
        ...createDealPayload,
        dealXgId: activeDealDetails?.dealId || '',
        dealXgVersion: dealStructure?.version || '',
        // Sent verbatim rather than reduced to a showroom boolean: there are three modes, and
        // collapsing them made a Sales Intake deal look like DC Sales Desking to the BFF.
        salesViewMode: (state.dealer?.dealerSettings as any)?.salesViewMode,
        dealSummary: {
            dealTypes: state.requestProposal.selectedDealTypes,
            incentives: state.requestProposal.selectedIncentives,
            notes: state.requestProposal.notes
        },
        ...(acceptedByBuyer !== undefined && acceptedByBuyer !== null ? { acceptedByBuyer } : {}),
        ...(visibility && !isEmpty(visibility) ? { visibility } : {}),
        ...(changeType ? { changeType } : {})
    };
};
